import React from 'react';
import { Box, ButtonBase, Card, CardActionArea, CardContent, LinearProgress, Typography } from '@mui/material';
import {
  BatteryAlert as BatteryAlertIcon,
  CheckCircle as CheckCircleIcon,
  CloudOff as CloudOffIcon,
  Thermostat as ThermostatIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  DifferentialNow,
  LocationStatus,
  MaturityDescriptor,
  PourSummary,
  Weather,
} from '../../../services/pours';
import { isHumidityLocation, isSiteConditionsChannel } from '../../../utils/locationChannels';
import { locationHasMaturity } from '../../../utils/locationMaturity';
import {
  formatMaturity,
  formatMaturityWithIndex,
  formatStrengthReading,
  pourHasCurve,
  resolveStrengthReading,
  strengthReadingTooltip,
} from '../../../utils/maturityDisplay';
import { locationStartsAt } from '../../../utils/onlineStatus';
import type { PourFunctionFlags } from '../../../utils/pourFunctionality';
import { formatAgo, type TranslateFn } from '../../../utils/relativeTime';
import { maturityAmountText } from '../../../utils/targetStatus';
import { TemperatureProfileBar, type TempLimitMarker } from '../TemperatureProfileBar';
import { nextTarget, type LocationHealth, type NextTargetInfo, type OverviewEntry } from './mobileDashboardModel';
import {
  alertWord,
  breachText,
  channelValue,
  commonNameStart,
  freshnessWord,
  limitNumber,
  limitsCaptionText,
  locationLabel,
  shortDateTime,
  staleOverText,
  toEpochMs,
} from './mobileText';
import { ALERT_LOOKS, AlertIcon, PILL_TONES, StatusPill } from './StatusPill';

/** What every card of one pour shares. */
export interface CardContext {
  pour: PourSummary;
  flags: PourFunctionFlags;
  meta: MaturityDescriptor;
  strengthUnit: string;
  /** The time of the data (last successful fetch): breach durations. */
  refNow: number;
  /** The wall clock (minute ticks): "Read 4 min ago". */
  now: number;
  /** The shared temperature scale of the profile bars. */
  referenceMax: number;
  scaleMax: number;
  weather?: Weather | null;
  onSelect: (locationId: string) => void;
}

const VALUE_SX = { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25, overflowWrap: 'anywhere' } as const;
const LABEL_SX = { fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 } as const;
const LINE_SX = { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' } as const;
/** A status word or count: never smaller than this. */
const WORD_SX = { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 } as const;

/** A value tile: label over value, with an optional caption (the unit or
 *  limit) and an optional status word on its own line ("Over limit"). */
const Tile: React.FC<{
  label: string;
  value: string;
  caption?: string | null;
  flag?: string | null;
  error?: boolean;
  testId?: string;
}> = ({ label, value, caption, flag, error = false, testId }) => (
  <Box data-testid={testId} sx={{ minWidth: 0 }}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: error ? 'error.main' : 'text.primary' }}>
      {error && <ThermostatIcon aria-hidden sx={{ fontSize: 18, flexShrink: 0 }} />}
      <Typography component="span" sx={{ ...VALUE_SX, color: 'inherit' }}>
        {value}
      </Typography>
    </Box>
    {caption && <Typography sx={LABEL_SX}>{caption}</Typography>}
    {flag && <Typography sx={{ ...WORD_SX, color: 'error.main', overflowWrap: 'anywhere' }}>{flag}</Typography>}
  </Box>
);

/** A bad-state chip: icon and words ("Battery 10%", "Weak signal"). */
const BadChip: React.FC<{ Icon: React.ComponentType<SvgIconProps>; text: string; tone: 'error' | 'warning' }> = ({
  Icon,
  text,
  tone,
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      minHeight: 28,
      px: 1,
      borderRadius: '999px',
      bgcolor: PILL_TONES[tone].bg,
      color: PILL_TONES[tone].fg,
      fontSize: '0.875rem',
      fontWeight: 600,
      maxWidth: '100%',
      overflowWrap: 'anywhere',
    }}
  >
    <Icon aria-hidden sx={{ fontSize: 16, flexShrink: 0 }} />
    {text}
  </Box>
);

/** The worst differential of a location: over its limit first, then the
 *  largest. */
export const worstLocationDifferential = (loc: LocationStatus): DifferentialNow | null => {
  const list = (loc.differentials ?? []).filter((d) => d.value != null || d.limit_value != null);
  if (list.length === 0) return null;
  return [...list].sort((a, b) => Number(b.over) - Number(a.over) || (b.value ?? -Infinity) - (a.value ?? -Infinity))[0];
};

/** The location's status word for its pill. */
export const locationAlertWord = (loc: LocationStatus, health: LocationHealth, t: TranslateFn, now: number): string =>
  alertWord(health.alert, t, { startsAt: locationStartsAt(loc), now });

/** "Read 4 min ago", "Delayed · read 15 min ago", "Starts 24 Oct, 06:00",
 *  "No readings received", with the colour of its freshness. */
export const readLine = (
  loc: LocationStatus,
  health: LocationHealth,
  t: TranslateFn,
  now: number,
  timezoneIana?: string | null,
): { text: string; color: string } => {
  const level = health.online.level;
  const tone = level === 'offline' ? PILL_TONES.error.fg : level === 'delayed' ? PILL_TONES.warning.fg : 'text.secondary';
  if (health.alert === 'scheduled') {
    const at = shortDateTime(locationStartsAt(loc), timezoneIana);
    return {
      text: at
        ? t('mobile.dashboard.card.startsAt', { defaultValue: 'Starts {{at}}', at })
        : t('mobile.dashboard.fresh.scheduled', { defaultValue: 'Scheduled' }),
      color: PILL_TONES.info.fg,
    };
  }
  if (health.online.awaiting) {
    return { text: t('mobile.dashboard.fresh.awaiting', { defaultValue: 'Awaiting first reading' }), color: PILL_TONES.info.fg };
  }
  if (!loc.last_read) {
    if (level === 'ended') return { text: freshnessWord('ended', t), color: 'text.secondary' };
    return {
      text: t('mobile.dashboard.card.noReadings', { defaultValue: 'No readings received' }),
      color: tone,
    };
  }
  const ago = formatAgo(loc.last_read, now, t);
  if (level === 'ended') {
    return {
      text: t('mobile.dashboard.card.lastRead', { defaultValue: 'Last read {{at}}', at: shortDateTime(loc.last_read, timezoneIana) }),
      color: 'text.secondary',
    };
  }
  if (level === 'online') {
    return { text: t('mobile.dashboard.card.readAgo', { defaultValue: 'Read {{ago}}', ago }), color: 'text.secondary' };
  }
  return {
    text: t('mobile.dashboard.card.staleRead', { defaultValue: '{{word}} · read {{ago}}', word: freshnessWord(level, t), ago }),
    color: tone,
  };
};

/** The next-target line: "Next: Stripping 25.0 MPa, 560.0 °C·hrs to go",
 *  or "All 4 targets reached". */
export const nextTargetText = (info: NextTargetInfo, meta: MaturityDescriptor, t: TranslateFn): string => {
  if (info.allReached) {
    return t('mobile.dashboard.card.allTargetsReached', {
      count: info.total,
      defaultValue_one: 'Target reached',
      defaultValue_other: 'All {{count}} targets reached',
    });
  }
  const target = [info.name, info.strengthText].filter(Boolean).join(' ')
    || (info.maturity !== null ? formatMaturity(info.maturity, meta) : '');
  if (info.status?.kind === 'pending' && info.remaining !== null && info.remaining > 0) {
    return t('mobile.dashboard.card.nextToGo', {
      defaultValue: 'Next: {{target}}, {{amount}} to go',
      target,
      amount: maturityAmountText(info.remaining, meta),
    });
  }
  return t('mobile.dashboard.card.nextStatus', {
    defaultValue: 'Next: {{target}} · {{status}}',
    target,
    status: info.status?.text ?? '',
  });
};

/** The strength (or, without a usable curve, the maturity with its index)
 *  of a maturity location, with the bounded strength's note. */
export const strengthOf = (
  loc: LocationStatus,
  pour: Pick<PourSummary, 'has_curve' | 'has_calibration' | 'calibration_check'>,
  meta: MaturityDescriptor,
  strengthUnit: string,
): { label: 'strength' | 'maturity'; value: string; note: string | null } => {
  const strengthShown = pourHasCurve(pour) !== false && pour.calibration_check?.status !== 'mismatch';
  if (strengthShown) {
    const reading = resolveStrengthReading(loc.strength, loc.current_strength);
    const text = formatStrengthReading(reading, strengthUnit, '');
    if (text) return { label: 'strength', value: text, note: strengthReadingTooltip(reading) };
  }
  return { label: 'maturity', value: formatMaturityWithIndex(loc.current_ea, meta, 1, '-'), note: null };
};

/** The location's limit markers for the profile bar (each also named in
 *  the caption under it: limitsCaptionText). */
const barMarkers = (health: LocationHealth): TempLimitMarker[] =>
  health.limits.map((l) => ({ temp: l.value, color: l.color ?? undefined, label: l.name ?? undefined }));

interface MobileLocationCardProps {
  entry: OverviewEntry;
  ctx: CardContext;
}

/**
 * One location on the Overview: one tap target that opens it on the
 * Location tab. Name and reading age, the status pill, Now / Max and one
 * function tile (differential or evaporation), the profile bar with its
 * visible limit caption, the limits broken now, the maturity line, and
 * bad-state chips only (battery, signal, the logger to check).
 */
export const MobileLocationCard: React.FC<MobileLocationCardProps> = ({ entry, ctx }) => {
  const { t } = useTranslation();
  const { location: loc, health } = entry;
  const { pour, flags, meta, strengthUnit, refNow, now } = ctx;
  const name = locationLabel(loc);
  const site = isSiteConditionsChannel(loc);
  const humidity = isHumidityLocation(loc);
  const ended = health.alert === 'ended';
  const showValues = health.alert !== 'scheduled' && !(health.online.awaiting && loc.current_temp == null);
  const tempBreach = health.breaches.some((b) => b.kind === 'temperature');
  const read = readLine(loc, health, t, now, pour.timezone_iana);

  // Now / Max, then one function tile: the differential (mass concrete),
  // else the evaporation rate.
  const tiles: React.ReactNode[] = [];
  if (showValues) {
    tiles.push(
      <Tile
        key="now"
        testId="tile-now"
        label={ended ? t('mobile.dashboard.card.last', { defaultValue: 'Last' }) : t('mobile.dashboard.card.now', { defaultValue: 'Now' })}
        value={channelValue(loc, loc.current_temp)}
        error={tempBreach}
      />,
      <Tile key="max" testId="tile-max" label={t('mobile.dashboard.card.max', { defaultValue: 'Max' })} value={channelValue(loc, loc.max_temp)} />,
    );
    const diff = site ? null : worstLocationDifferential(loc);
    if (diff) {
      const limit = diff.limit_value != null
        ? t('mobile.dashboard.card.diffLimit', { defaultValue: 'limit {{limit}} °C', limit: limitNumber(diff.limit_value) })
        : diff.partner_name
          ? t('mobile.dashboard.card.diffPartner', { defaultValue: 'vs {{partner}}', partner: diff.partner_name })
          : null;
      tiles.push(
        <Tile
          key="diff"
          testId="tile-diff"
          label={t('mobile.dashboard.card.diff', { defaultValue: 'Diff' })}
          value={diff.value != null ? `${diff.value.toFixed(1)} °C` : '-'}
          caption={limit}
          flag={diff.over ? t('mobile.dashboard.card.over', { defaultValue: 'Over' }) : null}
          error={diff.over}
        />,
      );
    } else if (!site && flags.evaporation && loc.evaporation_now != null) {
      tiles.push(
        <Tile
          key="evap"
          testId="tile-evap"
          label={t('mobile.dashboard.card.evap', { defaultValue: 'Evap' })}
          value={loc.evaporation_now.toFixed(2)}
          caption={t('mobile.dashboard.card.evapUnit', { defaultValue: 'kg/m²/h' })}
          flag={health.evaporationOver ? t('mobile.dashboard.card.overLimit', { defaultValue: 'Over limit' }) : null}
          error={health.evaporationOver}
        />,
      );
    }
  }

  // The profile bar of a concrete temperature channel, with every limit it
  // marks written out (the bar's own titles never show on touch).
  const showBar = showValues && !site && !humidity;
  const maturity = flags.maturity && locationHasMaturity(loc) && health.alert !== 'scheduled';
  const strength = maturity ? strengthOf(loc, pour, meta, strengthUnit) : null;
  const next = maturity ? nextTarget(loc, meta, { strengthUnit, timezoneIana: pour.timezone_iana, ended }) : null;

  const chips: React.ReactNode[] = [];
  const judged = health.alert !== 'ended' && health.alert !== 'scheduled';
  // Offline already shows in the pill and the read line: no third chip.
  if (health.alert !== 'offline' && health.sensorIssue && judged) {
    chips.push(
      <BadChip key="sensor" tone="warning" Icon={WarningIcon} text={t('mobile.dashboard.card.sensorLogger', { defaultValue: 'Sensor issue · Logger {{code}}', code: loc.logger_code })} />,
    );
  }
  if (judged && health.batteryLow && health.batteryPct !== null) {
    chips.push(
      <BadChip key="battery" tone="warning" Icon={BatteryAlertIcon} text={t('mobile.dashboard.card.battery', { defaultValue: 'Battery {{pct}}%', pct: Math.round(health.batteryPct) })} />,
    );
  }

  return (
    <Card variant="outlined" data-testid="location-card" data-location-id={loc.id} sx={{ borderRadius: 2, minWidth: 0 }}>
      <CardActionArea
        onClick={() => ctx.onSelect(loc.id)}
        aria-label={t('mobile.dashboard.card.open', { defaultValue: 'Open {{name}}', name })}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {name}
              </Typography>
              <Typography sx={{ ...LINE_SX, color: read.color }}>{read.text}</Typography>
            </Box>
            <StatusPill alert={health.alert} label={locationAlertWord(loc, health, t, now)} />
          </Box>

          {tiles.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))`, gap: 1 }}>
              {tiles}
            </Box>
          )}

          {showBar && (
            <Box>
              <TemperatureProfileBar
                simple
                maxMarker
                currentTemp={loc.current_temp}
                maxTemp={loc.max_temp}
                referenceMax={ctx.referenceMax}
                limits={barMarkers(health)}
                scaleMax={ctx.scaleMax}
                sx={{ height: 8 }}
              />
              {health.limits.length > 0 && (
                <Typography sx={{ ...LABEL_SX, textAlign: 'right', mt: 0.5 }} data-testid="limit-caption">
                  {limitsCaptionText(health.limits, t)}
                </Typography>
              )}
            </Box>
          )}

          {health.breaches.map((b, i) => (
            <Box key={`${b.kind}-${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: 'error.main' }}>
              <ThermostatIcon aria-hidden sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ ...LINE_SX, color: 'inherit' }}>{breachText(b, t, refNow)}</Typography>
            </Box>
          ))}

          {health.staleOver && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: PILL_TONES.error.fg }}>
              <CloudOffIcon aria-hidden sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ ...LINE_SX, color: 'inherit' }}>
                {staleOverText(health.staleOver, t, health.staleOver.at !== null ? formatAgo(health.staleOver.at, now, t) : '')}
              </Typography>
            </Box>
          )}

          {strength && (
            <Box data-testid="maturity-row" sx={{ minWidth: 0 }}>
              <Typography component="div" sx={{ fontSize: '1rem', fontWeight: 600, overflowWrap: 'anywhere' }}>
                {strength.label === 'strength' && (
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    {`${t('mobile.dashboard.card.strength', { defaultValue: 'Strength' })} `}
                  </Box>
                )}
                <span>{strength.value}</span>
              </Typography>
              {strength.note && (
                <Typography sx={{ ...LABEL_SX }}>{strength.note}</Typography>
              )}
              {next && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25 }}>
                  {next.allReached && <CheckCircleIcon aria-hidden sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />}
                  <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                    {nextTargetText(next, meta, t)}
                  </Typography>
                </Box>
              )}
              {next && !next.allReached && next.progress !== null && (
                <LinearProgress
                  variant="determinate"
                  value={Math.round(next.progress * 100)}
                  aria-label={t('mobile.dashboard.card.progress', { defaultValue: 'Progress to the next target' })}
                  sx={{ height: 4, borderRadius: 2, mt: 0.75 }}
                />
              )}
            </Box>
          )}

          {chips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{chips}</Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/** One location as a compact row of at least 44px (pours with more than 8
 *  locations, after Needs attention): the name with Max under it, Now and
 *  the status word, so the name keeps its room on a narrow phone. */
export const CompactLocationRow: React.FC<MobileLocationCardProps> = ({ entry, ctx }) => {
  const { t } = useTranslation();
  const { location: loc, health } = entry;
  const name = locationLabel(loc);
  const showValues = health.alert !== 'scheduled';
  return (
    <ButtonBase
      onClick={() => ctx.onSelect(loc.id)}
      aria-label={t('mobile.dashboard.card.open', { defaultValue: 'Open {{name}}', name })}
      data-testid="compact-row"
      sx={{
        width: '100%',
        minHeight: 44,
        px: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        textAlign: 'left',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <AlertIcon alert={health.alert} />
      <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
        <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
          {name}
        </Typography>
        {showValues && (
          <Typography noWrap sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            {t('mobile.dashboard.card.maxShort', { defaultValue: 'Max {{value}}', value: channelValue(loc, loc.max_temp) })}
          </Typography>
        )}
      </Box>
      {showValues && (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {channelValue(loc, loc.current_temp)}
        </Typography>
      )}
      <Typography sx={{ ...WORD_SX, whiteSpace: 'nowrap', color: PILL_TONES[ALERT_LOOKS[health.alert].tone].fg }}>
        {locationAlertWord(loc, health, t, ctx.now)}
      </Typography>
    </ButtonBase>
  );
};

interface AmbientLoggerCardProps {
  loggerCode: string;
  entries: OverviewEntry[];
  ctx: CardContext;
}

/**
 * The channels of one ambient Temp & Humidity logger on one card: a 44px
 * row per channel (name, Now, Max, status word), each opening its channel.
 * Ambient channels have no limits, so no limit state ever shows.
 */
export const AmbientLoggerCard: React.FC<AmbientLoggerCardProps> = ({ loggerCode, entries, ctx }) => {
  const { t } = useTranslation();
  const names = entries.map((e) => locationLabel(e.location));
  const title = commonNameStart(names)
    || t('mobile.dashboard.card.ambientTitle', { defaultValue: 'Ambient Temp & Humidity' });
  // The newest reading of the logger's channels.
  const newest = entries.reduce<OverviewEntry | null>((best, e) => {
    const at = toEpochMs(e.location.last_read);
    const bestAt = best ? toEpochMs(best.location.last_read) : null;
    return at !== null && (bestAt === null || at > bestAt) ? e : best;
  }, null);
  const read = newest ? readLine(newest.location, newest.health, t, ctx.now, ctx.pour.timezone_iana) : null;

  return (
    <Card variant="outlined" data-testid="ambient-card" sx={{ borderRadius: 2, minWidth: 0 }}>
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography sx={{ ...LABEL_SX }}>
          {t('mobile.dashboard.card.logger', { defaultValue: 'Logger {{code}}', code: loggerCode })}
        </Typography>
        {read && <Typography sx={{ ...LINE_SX, color: read.color }}>{read.text}</Typography>}
      </Box>
      {entries.map(({ location: loc, health }) => {
        const name = locationLabel(loc);
        const showValues = health.alert !== 'scheduled' && loc.current_temp != null;
        return (
          <ButtonBase
            key={loc.id}
            onClick={() => ctx.onSelect(loc.id)}
            aria-label={t('mobile.dashboard.card.open', { defaultValue: 'Open {{name}}', name })}
            data-testid="ambient-row"
            sx={{
              width: '100%',
              minHeight: 44,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textAlign: 'left',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AlertIcon alert={health.alert} />
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.9375rem', fontWeight: 600 }}>
              {name}
            </Typography>
            {showValues && (
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {channelValue(loc, loc.current_temp)}
              </Typography>
            )}
            {showValues && loc.max_temp != null && (
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {t('mobile.dashboard.card.maxShort', { defaultValue: 'Max {{value}}', value: channelValue(loc, loc.max_temp) })}
              </Typography>
            )}
            {health.alert !== 'ok' && health.alert !== 'reporting' && (
              <Typography sx={{ ...WORD_SX, whiteSpace: 'nowrap', color: PILL_TONES[ALERT_LOOKS[health.alert].tone].fg }}>
                {locationAlertWord(loc, health, t, ctx.now)}
              </Typography>
            )}
          </ButtonBase>
        );
      })}
    </Card>
  );
};

/** A card's place while the dashboard loads. */
export const LocationCardSkeleton: React.FC = () => (
  <Card variant="outlined" data-testid="location-card-skeleton" sx={{ borderRadius: 2, p: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Box sx={{ width: '50%' }}>
        <Box sx={{ height: 20, bgcolor: 'grey.100', borderRadius: 1, mb: 0.75 }} />
        <Box sx={{ height: 14, width: '70%', bgcolor: 'grey.100', borderRadius: 1 }} />
      </Box>
      <Box sx={{ width: 72, height: 28, bgcolor: 'grey.100', borderRadius: '999px' }} />
    </Box>
    <Box sx={{ height: 40, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }} />
    <Box sx={{ height: 8, bgcolor: 'grey.100', borderRadius: 1 }} />
  </Card>
);

export default MobileLocationCard;
